import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { useCallback } from 'react';

import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { planListExportDocsForKno } from 'PlanListPackage/MainModule/actions/planListActions';
import { IconMenu } from 'UIPackage/IconsModule/components/IconMenu';
import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';
import { IDropdownItem } from 'UIPackage/MainModule/interfaces/IDropdownItem';
import { ExportFormatsEnum } from 'APIPackage/ImportExportModule/enums/ExportFormatsEnum';
import { ExportSimilaritiesListTypesEnum } from 'APIPackage/SimilarityModule/enums/ExportSimilaritiesListTypesEnum';

import styles from './ImportDropdown.module.scss';
import { IImportRow } from '../interfaces/IImportRow';
import { importFileStatuses } from '../constants/importFileStatuses';
import { importExportSimilaritiesListByPlanId } from '../actions/importActions';

interface IImportDropdownProps {
  onDelete: (id: IImportRow['id']) => void;
  data: IImportRow;
}

export const ImportDropdown = ({ data, onDelete }: IImportDropdownProps) => {
  const { id, fileType, fileStatus, planId } = data;
  const fileStatusCode = importFileStatuses.find(status => status.name === fileStatus)?.id;
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const handleRemoveClick = useCallback(() => onDelete(id), [id, onDelete]);

  const exportDocuments = useCallback(() => {
    if (planId) {
      dispatch(planListExportDocsForKno(planId));
    }
  }, [dispatch, planId]);

  const handleExportSimilaritiesKnmList = (
    format: ExportFormatsEnum,
    type: ExportSimilaritiesListTypesEnum.KNM_MATCH_BY_PLAN | ExportSimilaritiesListTypesEnum.KNM_MATCH_BY_PLAN_ALL,
  ) => () => {
    dispatch(importExportSimilaritiesListByPlanId(String(planId), format, type));
  };

  const exportSimByPlanAllODS = handleExportSimilaritiesKnmList(
    ExportFormatsEnum.ODS,
    ExportSimilaritiesListTypesEnum.KNM_MATCH_BY_PLAN_ALL,
  );
  const exportSimByPlanAllXLSX = handleExportSimilaritiesKnmList(
    ExportFormatsEnum.XLSX,
    ExportSimilaritiesListTypesEnum.KNM_MATCH_BY_PLAN_ALL,
  );
  const exportSimByPlanODS = handleExportSimilaritiesKnmList(
    ExportFormatsEnum.ODS,
    ExportSimilaritiesListTypesEnum.KNM_MATCH_BY_PLAN,
  );
  const exportSimByPlanXLSX = handleExportSimilaritiesKnmList(
    ExportFormatsEnum.XLSX,
    ExportSimilaritiesListTypesEnum.KNM_MATCH_BY_PLAN,
  );
  let dropdownItems: IDropdownItem[] = [
    {
      id: 'import-item-dropdown-delete-record',
      content: <DropdownItem onClick={handleRemoveClick}>Удалить запись</DropdownItem>,
    },
  ];

  if (fileStatusCode === 'NEW') {
    if (fileType === 'CHECKLIST') {
      dropdownItems = [
        ...dropdownItems,
        {
          id: 'divider-1',
          isDivider: true,
        },
        {
          id: 'import-item-dropdown-delete',
          content: <DropdownItem onClick={handleRemoveClick}>Удалить запись</DropdownItem>,
        },
      ];
    }
  } else if (fileStatusCode === 'CREATED_WARNINGS' || fileStatusCode === 'CREATED_SUCCESS') {
    if (planId) {
      dropdownItems = [
        ...dropdownItems,
        {
          id: 'divider-1',
          isDivider: true,
        },
        {
          id: 'import-item-dropdown-1',
          content: <DropdownItem onClick={exportDocuments}>Экспорт комплекта документов для КНО</DropdownItem>,
        },
        {
          id: 'import-item-dropdown-2',
          content: (
            <DropdownItem onClick={exportSimByPlanAllODS}>Вывести совпадения по плану (По всей РФ) в ODS</DropdownItem>
          ),
        },
        {
          id: 'import-item-dropdown-3',
          content: (
            <DropdownItem onClick={exportSimByPlanAllXLSX}>
              Вывести совпадения по плану (По всей РФ) в XLSX
            </DropdownItem>
          ),
        },
        {
          id: 'import-item-dropdown-4',
          content: (
            <DropdownItem onClick={exportSimByPlanODS}>Вывести совпадения по плану (По субъекту РФ) в ODS</DropdownItem>
          ),
        },
        {
          id: 'import-item-dropdown-5',
          content: (
            <DropdownItem onClick={exportSimByPlanXLSX}>
              Вывести совпадения по плану (По субъекту РФ) в XLSX
            </DropdownItem>
          ),
        },
      ];
    }
  }

  return (
    <Dropdown items={dropdownItems}>
      <button className={styles.DropdownMenuButton} type="button">
        <IconMenu className={styles.MenuButtonIcon} />
      </button>
    </Dropdown>
  );
};
